<script lang="ts" setup>
import type { UseScrollReturn } from '@vueuse/core'
import { vScroll } from '@vueuse/components'

const props = defineProps<{
  name: string
}>()

const gallery = useTemplateRef('gallery')

const {
  galleryOptions,
  galleryItems,
  activeIndex,
  activeItem,
  getImageFromProvider,
  scrollBehavior,
  enlarged,
  zoomLevel,
  zoomPan,
  zoomReset,
} = useUiMediaGallery(props.name)

const galleryName = 'media-gallery'
const mediaGalleryEnlarge = useUiDialog(galleryName)

// Reset zoom whenever the active image changes or fullscreen closes, so the
// next image (or re-opening) always starts back at the default zoom level.
watch(activeIndex, () => zoomReset())
watch(() => mediaGalleryEnlarge.settings.value.isOpen, (isOpen) => {
  if (!isOpen) {
    zoomReset()
  }
})

// --- Drag to navigate (desktop, left mouse button) --------------------
// Lets a mouse user click-and-drag the slider to move between images,
// the same way touch users can already swipe.
const isSlideDragging = ref(false)
let slideDragMoved = false
let slideDragStartX = 0
let slideDragStartScrollLeft = 0

function onSlideDragStart(event: MouseEvent) {
  if (event.button !== 0 || enlarged.value || !gallery.value) {
    return
  }

  isSlideDragging.value = true
  slideDragMoved = false
  slideDragStartX = event.clientX
  slideDragStartScrollLeft = gallery.value.scrollLeft

  window.addEventListener('mousemove', onSlideDragMove)
  window.addEventListener('mouseup', onSlideDragEnd)
}

function onSlideDragMove(event: MouseEvent) {
  if (!isSlideDragging.value || !gallery.value) {
    return
  }

  const delta = event.clientX - slideDragStartX
  if (Math.abs(delta) > 3) {
    slideDragMoved = true
  }

  gallery.value.scrollLeft = slideDragStartScrollLeft - delta
}

function onSlideDragEnd() {
  isSlideDragging.value = false
  window.removeEventListener('mousemove', onSlideDragMove)
  window.removeEventListener('mouseup', onSlideDragEnd)
}

// --- Drag to pan the zoomed-in image ------------------------------------
const isPanDragging = ref(false)
let panDragStartX = 0
let panDragStartY = 0
let panStart = { x: 0, y: 0 }

function getPanBounds(target: HTMLElement) {
  const item = target.closest('.media-gallery-main__item') as HTMLElement | null
  const content = target.closest('.media-gallery-main__content') as HTMLElement | null

  if (!item || !content) {
    return { x: 0, y: 0 }
  }

  const itemRect = item.getBoundingClientRect()
  const contentWidth = content.offsetWidth * zoomLevel.value
  const contentHeight = content.offsetHeight * zoomLevel.value

  const overflowX = Math.max(0, contentWidth - itemRect.width)
  const overflowY = Math.max(0, contentHeight - itemRect.height)

  return {
    x: (overflowX / 2) / zoomLevel.value,
    y: (overflowY / 2) / zoomLevel.value,
  }
}

function onPanDragStart(event: PointerEvent) {
  if ((event.pointerType === 'mouse' && event.button !== 0) || !enlarged.value) {
    return
  }

  event.preventDefault()

  isPanDragging.value = true
  panDragStartX = event.clientX
  panDragStartY = event.clientY
  panStart = { ...zoomPan.value }

  const target = event.currentTarget as HTMLElement
  const pointerId = event.pointerId

  target.setPointerCapture(pointerId)

  function onMove(moveEvent: PointerEvent) {
    if (moveEvent.pointerId !== pointerId) {
      return
    }

    const bounds = getPanBounds(target)
    const dx = (moveEvent.clientX - panDragStartX) / zoomLevel.value
    const dy = (moveEvent.clientY - panDragStartY) / zoomLevel.value

    zoomPan.value = {
      x: Math.min(bounds.x, Math.max(-bounds.x, panStart.x + dx)),
      y: Math.min(bounds.y, Math.max(-bounds.y, panStart.y + dy)),
    }
  }

  function onUp(upEvent: PointerEvent) {
    if (upEvent.pointerId !== pointerId) {
      return
    }

    isPanDragging.value = false
    target.releasePointerCapture(pointerId)
    target.removeEventListener('pointermove', onMove)
    target.removeEventListener('pointerup', onUp)
    target.removeEventListener('pointercancel', onUp)
  }

  target.addEventListener('pointermove', onMove)
  target.addEventListener('pointerup', onUp)
  target.addEventListener('pointercancel', onUp)
}

// --- Scroll wheel pan (desktop) -------------------------------------------
// Lets a mouse user scroll to move around the zoomed-in image, the same
// way they could move it before zoom/pan was reworked as a transform.
function onWheelPan(event: WheelEvent) {
  if (!enlarged.value || !gallery.value) {
    return
  }

  const content = gallery.value.querySelector(
    `[data-item="${activeIndex.value}"] .media-gallery-main__content`,
  ) as HTMLElement | null

  if (!content) {
    return
  }

  event.preventDefault()

  const bounds = getPanBounds(content)
  const dx = event.deltaX / zoomLevel.value
  const dy = event.deltaY / zoomLevel.value

  zoomPan.value = {
    x: Math.min(bounds.x, Math.max(-bounds.x, zoomPan.value.x - dx)),
    y: Math.min(bounds.y, Math.max(-bounds.y, zoomPan.value.y - dy)),
  }
}

let timer: ReturnType<typeof setTimeout> | null = null
function onScroll(state: UseScrollReturn) {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }

  timer = setTimeout(function () {
    onScrollEnd(state)
  }, 50)
}

function onScrollEnd(state: UseScrollReturn) {
  if (!gallery.value) {
    return
  }

  const galleryWidth = gallery.value.offsetWidth

  const scrollLeft = state.x.value
  const scrollRight = state.x.value + galleryWidth

  const activeItem = galleryItems.value.find((item) => {
    const itemCenter = (item.index + 1) * galleryWidth - galleryWidth / 2
    return itemCenter > scrollLeft && itemCenter < scrollRight
  }) || null

  activeIndex.value = activeItem?.index || 0
}

watchEffect(() => {
  const elm = gallery.value?.querySelector(
    `[data-item="${activeIndex.value}"]`,
  ) as HTMLElement

  gallery.value?.scroll({
    top: 0,
    left: elm?.offsetLeft || 0,
    behavior: scrollBehavior.value,
  })
})

function onClick(event: MouseEvent) {
  // Ignore the click that follows a slide drag, so dragging doesn't
  // accidentally open fullscreen or jump to a different image.
  if (slideDragMoved) {
    slideDragMoved = false
    return
  }

  if (enlarged.value) {
    return
  }

  const target = event.currentTarget as HTMLElement
  const index = Number(target.dataset.item)

  scrollBehavior.value = 'instant'
  activeIndex.value = index
  mediaGalleryEnlarge.open()

  nextTick(() => {
    scrollBehavior.value = 'smooth'
  })
}
</script>

<template>
  <div
    ref="gallery"
    v-bind="$attrs"
    v-scroll="onScroll"
    class="media-gallery-main"
    :class="{
      'media-gallery-main--dragging': isSlideDragging,
      'media-gallery-main--panning': isPanDragging,
    }"
    @mousedown="onSlideDragStart"
    @wheel="onWheelPan"
  >
    <div
      v-for="item in galleryItems"
      :key="item.src"
      class="media-gallery-main__item"
      :data-item="item.index"
      @click="onClick"
    >
      <div
        class="media-gallery-main__content"
        :class="{ 'media-gallery-main__content--zoomed': enlarged && activeItem?.index === item.index }"
        :style="activeItem?.index === item.index && enlarged
          ? { transform: `scale(${zoomLevel}) translate(${zoomPan.x}px, ${zoomPan.y}px)` }
          : undefined"
        @pointerdown="onPanDragStart"
      >
        <UiVideo
          v-if="item.type === 'video'"
          :src="item.src"
          width="1920"
          height="1080"
          :autoplay="activeItem?.index === item.index"
          :controls="true"
          playsinline
          loop
          muted
          class="media-gallery-main__video"
        />
        <picture v-else-if="item.type === 'image'">
          <source
            :srcset="getImageFromProvider(item, { width: (enlarged ? 1920 : 1080) })"
            media="(min-width: 768px)"
          >
          <source :srcset="getImageFromProvider(item, { width: (enlarged ? 1920 : 768) })">
          <img
            :src="getImageFromProvider(item, { width: (enlarged ? 1920 : 1080) })"
            :alt="item.alt"
            width="1920"
            height="1080"
            :loading="item.index ? 'lazy' : 'eager'"
            :fetchpriority="item.index ? 'low' : 'high'"
            :decoding="item.index ? 'async' : 'sync'"
            class="media-gallery-main__img"
          >
        </picture>
      </div>
    </div>

    <slot name="gallery-items" />
  </div>

  <button
    type="button"
    class="media-gallery__show-all"
    @click="galleryOptions.showAll = !galleryOptions.showAll"
  >
    <span>
      {{ galleryOptions.showAll ? $t('Collapse') : $t('View all photos') }}
    </span>
  </button>
</template>

<style>
@import url("~layers/components/Ui/MediaGallery/Main.css");
</style>
